import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';

import * as K from 'common/constants';
import ELEMENT, { ElementKey } from 'common/element';
import { PlotModel } from 'common/types';

import TruncateWithTooltip, { TooltipPlacement } from 'components/common/TruncateWithTooltip/TruncateWithTooltip';

import './PlotGroupPicker.scss';
import { searchLegendWidthSelector } from 'components/common/Managers/LayoutStateManager';
import { useRecoilValue } from 'recoil';

export type LegendPlotParams = {
  visible: boolean;
  name: string;
  value: number;
  color: string;
};

type PlotGroupPickerProps = {
  title: string;
  plots: Array<PlotModel>;
  capitalizeLabels?: boolean;
  togglePlot: (plotToToggle: PlotModel) => void;
  togglePlotGroup: (title: string, toggleState: boolean) => void;
};

const FONT_CONSTANT = 1.91; // font constant - sans serif helvetica
const FONT_SIZE = 12;
const CHECKBOX_PADDING = 12; // approx character count of checkbox width + padding
/**
 * @prop  title                title of the section of plots, will serve to toggle on/off entire section
 * @prop  plots                List of plot objects modeling the current plot data visibility as well as
 *                             hovered/selected values. TODO: update values based on hover and selection of graph
 * @prop  togglePlot           function to toggle on/off a specific plot's visibility.
 * @prop  togglePlotGroup      function to toggle on/off a group of plots
 */
function PlotGroupPicker({
  title,
  plots,
  capitalizeLabels = false,
  togglePlot,
  togglePlotGroup,
}: PlotGroupPickerProps): JSX.Element {
  const allVisible = plots.every((plot) => plot.visible);

  // Toggle on and off a specific plot within this group of plots
  const handlePlotRowClick = (plotToToggle: PlotModel) => (): void => {
    togglePlot(plotToToggle);
  };

  // Toggle on and off the visibility for the entire group of plots
  const toggleGroup = (): void => {
    togglePlotGroup(title, !allVisible);
  };

  const modifierClass = capitalizeLabels ? 'PlotGroupPicker__label--capitalize' : '';

  const testIdBase = ('searchGraphPlotGroup' + upperFirst(camelCase(title))) as ElementKey;
  const groupCheckbox = allVisible
    ? <CheckBox style={{ fontSize: K.GRAPH_LEGEND_ICON_SIZE }} />
    : <CheckBoxOutlineBlank style={{ fontSize: K.GRAPH_LEGEND_ICON_SIZE }} />;

  const pickerTestId = ELEMENT[testIdBase];

  const getPlotTestId = (plot: PlotModel): ELEMENT => ELEMENT[testIdBase + upperFirst(camelCase(plot.op || plot.name)) as ElementKey];
  const legendWidth = useRecoilValue(searchLegendWidthSelector);
  // Track the value with the most digits to aid with ellpsis calculations.
  let longestValueWidth = 0;
  plots.forEach(plot => {
    const value = plot?.value ? plot.value : '0';
    longestValueWidth = Math.max(value.toString().length, longestValueWidth);
  });
  // formulate to calculate characters per line - CPL = width * font-constant / font-size
  // difference is to remove width occupied by plot values and checkbox
  const charactersPerLine = Math.round(legendWidth * FONT_CONSTANT / FONT_SIZE) - (longestValueWidth + CHECKBOX_PADDING);
  const facetDisplayNameMaxLength = charactersPerLine > 0 ? charactersPerLine : K.FACET_DISPLAY_NAME_MAX_LEN;
  return (
    <div className="PlotGroupPicker">
      <div
        className="PlotGroupPicker__titleContainer"
        role="presentation"
        onClick={toggleGroup}
        data-test-id={pickerTestId}
      >
        {groupCheckbox}
        <span className="PlotGroupPicker__title">
          <TruncateWithTooltip
            text={title || ''}
            id={title + '-PlotGroupPickerTitle'}
            maxLength={facetDisplayNameMaxLength}
            placement={TooltipPlacement.TOP}
            ellipsisPlacement={K.PLACEMENT.MIDDLE}
          />
        </span>
      </div>
      <ul className="PlotGroupPicker__plotList" >
        {
          plots.map((plot, idx) => (
            <li
              className="PlotGroupPicker__plotRow"
              key={plot.name + `-${idx}`}
              onClick={handlePlotRowClick(plot)}
              data-test-id={getPlotTestId(plot)}
            >
              <div className="PlotGroupPicker__labelContainer">
                {
                  plot.visible
                    ? <CheckBox style={{ color: plot.color, fontSize: K.GRAPH_LEGEND_ICON_SIZE }} />
                    : <CheckBoxOutlineBlank style={{ fontSize: K.GRAPH_LEGEND_ICON_SIZE }} />
                }
              <span className={classNames('PlotGroupPicker__label', modifierClass)}>
                  <TruncateWithTooltip
                    text={plot.name || ''}
                    id={plot.name + `-${idx}`}
                    maxLength={facetDisplayNameMaxLength}
                    placement={TooltipPlacement.TOP}
                    ellipsisPlacement={K.PLACEMENT.MIDDLE}
                  />
                </span>
              </div>
              <div>{plot.value}</div>
            </li>
          ))
        }
      </ul>
    </div>
  );
}

export default PlotGroupPicker;
